import React, { useEffect } from 'react';
import type { SliderScaleConfig } from '../../components/survey/types';
import { isSliderScaleAnswer, type SliderScaleAnswer } from './sliderScale';

interface Props {
  config: SliderScaleConfig;
  value: unknown;
  onChange: (answer: SliderScaleAnswer) => void;
}

// The filled and empty parts of the track: blue-600 (the brand teal, per tailwind.config) / gray-200.
const TRACK_FILL = '#02b9a8';
const TRACK_EMPTY = '#e5e7eb';

export const SLIDER_VALUE_PREFIX_MAX_LENGTH = 8;

// Written out in full: Tailwind only generates classes it can find literally in the source.
const RANGE_CLASS = [
  'w-full h-2 rounded-full appearance-none cursor-pointer bg-transparent focus:outline-none',
  '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6',
  '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-solid',
  '[&::-webkit-slider-thumb]:border-blue-600 [&::-webkit-slider-thumb]:shadow-md',
  '[&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110',
  '[&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:rounded-full',
  '[&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-solid [&::-moz-range-thumb]:border-blue-600',
  '[&::-moz-range-thumb]:shadow-md [&::-moz-range-track]:bg-transparent',
  'focus-visible:[&::-webkit-slider-thumb]:ring-4 focus-visible:[&::-webkit-slider-thumb]:ring-blue-200',
  'focus-visible:[&::-moz-range-thumb]:ring-4 focus-visible:[&::-moz-range-thumb]:ring-blue-200',
].join(' ');

/** 0–100-style slider that starts at its default; records whether the respondent moved it. */
const SliderScaleInput: React.FC<Props> = ({ config, value, onChange }) => {
  const answer = isSliderScaleAnswer(value) ? value : null;

  // No dependency list on purpose: the session clears the response when a question opens, after
  // this child's effect has run, so the default is re-applied on whichever render finds it missing.
  useEffect(() => {
    if (!isSliderScaleAnswer(value)) onChange({ value: config.default, touched: false });
  });

  const current = answer?.value ?? config.default;
  const touched = answer?.touched ?? false;
  const prefix = (config.value_prefix ?? '').trim().slice(0, SLIDER_VALUE_PREFIX_MAX_LENGTH);
  const fillPercent = ((current - config.min) / (config.max - config.min)) * 100;

  return (
    <div className="mx-auto max-w-xl space-y-4 px-1 py-2">
      <div
        className={`text-center text-4xl font-semibold tabular-nums transition-colors ${
          touched ? 'text-gray-900' : 'text-gray-400'
        }`}
      >
        {prefix}
        {current}
      </div>
      <input
        type="range"
        min={config.min}
        max={config.max}
        step={config.step ?? 1}
        value={current}
        onChange={(e) => onChange({ value: Number(e.target.value), touched: true })}
        className={RANGE_CLASS}
        style={{
          background: `linear-gradient(to right, ${TRACK_FILL} ${fillPercent}%, ${TRACK_EMPTY} ${fillPercent}%)`,
        }}
        aria-valuemin={config.min}
        aria-valuemax={config.max}
        aria-valuenow={current}
      />
      <div className="flex justify-between gap-4 text-sm text-gray-500">
        <span className="max-w-[45%] text-left">{config.min_label ?? config.min}</span>
        <span className="max-w-[45%] text-right">{config.max_label ?? config.max}</span>
      </div>
    </div>
  );
};

export default SliderScaleInput;
